import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/components/ui/dialog";
import { Button } from "@shared/components/ui/button";
import { Badge } from "@shared/components/ui/badge";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { UserCheck, UserX, Truck, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import * as packagesService from "../services/packagesService";
import { SelectUser } from "@/features/users/SelectUser";

export function LivreurAssignmentDialog({ packageData, trigger, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [selectedLivreur, setSelectedLivreur] = useState("");
  const [selectedLivreurData, setSelectedLivreurData] = useState(null);
  const [deliveryPrice, setDeliveryPrice] = useState(
    packageData?.livreurDeliveryPrice || ""
  );
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Get available livreurs
  const { data: livreurs, isLoading: isLoadingLivreurs } = useQuery({
    queryKey: ["livreurs"],
    queryFn: packagesService.getAllLivreurs,
    enabled: open,
  });

  // Get default price when livreur is selected
  const getDefaultPrice = () => {
    if (selectedLivreurData?.livreurPricing?.defaultDeliveryPrice) {
      return selectedLivreurData.livreurPricing.defaultDeliveryPrice;
    }
    return 10; // Fallback default
  };

  // Assign livreur mutation
  const assignLivreurMutation = useMutation({
    mutationFn: (data) =>
      packagesService.assignLivreurToPackage(packageData._id, data),
    onSuccess: () => {
      // Invalidate package-related queries
      queryClient.invalidateQueries({ queryKey: ["packages"] });
      queryClient.invalidateQueries({ queryKey: ["package", packageData._id] });
      queryClient.invalidateQueries({ queryKey: ["livreur-packages"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });

      // Invalidate admin livreur management queries
      queryClient.invalidateQueries({ queryKey: ["admin-packages"] });
      queryClient.invalidateQueries({ queryKey: ["admin-livreurs"] });

      // Invalidate pickup-related queries that might show package assignments
      queryClient.invalidateQueries({ queryKey: ["pickups"] });
      queryClient.invalidateQueries({ queryKey: ["pickup-stats"] });
      queryClient.invalidateQueries({ queryKey: ["pickup-analytics"] });
      queryClient.invalidateQueries({ queryKey: ["recent-pickups"] });
      queryClient.invalidateQueries({ queryKey: ["livreur-pickups-widget"] });

      // Invalidate livreur dashboard queries
      queryClient.invalidateQueries({ queryKey: ["livreur-dashboard"] });

      toast.success(t("livreur.livreurAssignedSuccessfully"));
      setOpen(false);
      setSelectedLivreur("");
      setDeliveryPrice(0);

      // Call optional success callback
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || t("livreur.failedToAssignLivreur")
      );
    },
  });

  // Unassign livreur mutation
  const unassignLivreurMutation = useMutation({
    mutationFn: (reason) =>
      packagesService.unassignLivreurFromPackage(packageData._id, reason),
    onSuccess: () => {
      // Invalidate package-related queries
      queryClient.invalidateQueries({ queryKey: ["packages"] });
      queryClient.invalidateQueries({ queryKey: ["package", packageData._id] });
      queryClient.invalidateQueries({ queryKey: ["livreur-packages"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });

      // Invalidate admin livreur management queries
      queryClient.invalidateQueries({ queryKey: ["admin-packages"] });
      queryClient.invalidateQueries({ queryKey: ["admin-livreurs"] });

      // Invalidate pickup-related queries that might show package assignments
      queryClient.invalidateQueries({ queryKey: ["pickups"] });
      queryClient.invalidateQueries({ queryKey: ["pickup-stats"] });
      queryClient.invalidateQueries({ queryKey: ["pickup-analytics"] });
      queryClient.invalidateQueries({ queryKey: ["recent-pickups"] });
      queryClient.invalidateQueries({ queryKey: ["livreur-pickups-widget"] });

      // Invalidate livreur dashboard queries
      queryClient.invalidateQueries({ queryKey: ["livreur-dashboard"] });

      toast.success(t("livreur.livreurUnassignedSuccessfully"));
      setOpen(false);

      // Call optional success callback
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || t("livreur.failedToUnassignLivreur")
      );
    },
  });

  const handleAssign = () => {
    if (!selectedLivreur) {
      toast.error(t("livreur.pleaseSelectALivreur"));
      return;
    }

    // Use entered price or default
    const finalPrice =
      deliveryPrice && deliveryPrice > 0
        ? Number(deliveryPrice)
        : getDefaultPrice();

    assignLivreurMutation.mutate({
      livreurId: selectedLivreur,
      livreurDeliveryPrice: finalPrice,
    });
  };

  // Update price when livreur changes
  const handleLivreurSelect = (livreurId) => {
    setSelectedLivreur(livreurId);
    const livreur = livreurs?.find((l) => l._id === livreurId);
    setSelectedLivreurData(livreur);

    // Set default price if no custom price entered
    if (!deliveryPrice) {
      const defaultPrice = livreur?.livreurPricing?.defaultDeliveryPrice || 10;
      setDeliveryPrice(defaultPrice);
    }
  };

  const handleUnassign = () => {
    unassignLivreurMutation.mutate("Unassigned by admin");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen} className="z-10">
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            {t("livreur.manageLivreurAssignment")}
          </DialogTitle>
          <DialogDescription>
            {packageData.livreur
              ? t("livreur.thisPackageIsCurrentlyAssignedToALivreur")
              : t("livreur.assignALivreurToHandleThisPackageDelivery")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Current Assignment Status */}
          <div className="p-4 rounded-lg border bg-muted/50">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                {t("livreur.currentStatus")}:
              </span>
              {packageData.livreur ? (
                <Badge variant="default" className="flex items-center gap-1">
                  <UserCheck className="h-3 w-3" />
                  {t("pickup.assigned")}
                </Badge>
              ) : (
                <Badge variant="secondary" className="flex items-center gap-1">
                  <UserX className="h-3 w-3" />
                  {t("livreur.unassigned")}
                </Badge>
              )}
            </div>

            {packageData.livreur && (
              <div className="mt-2 space-y-1">
                <p className="text-sm">
                  <span className="font-medium">{t("livreur.livreur")}:</span>{" "}
                  {packageData.livreur.fullName || packageData.livreur.email}
                </p>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium">{t("livreur.phone")}:</span>{" "}
                  {packageData.livreur.phone}
                </p>
                {packageData.livreurDeliveryPrice > 0 && (
                  <p className="text-sm">
                    <span className="font-medium">
                      {t("livreur.deliveryPrice")}:
                    </span>{" "}
                    <span className="text-primary font-semibold">
                      {packageData.livreurDeliveryPrice} DH
                    </span>
                  </p>
                )}
                {packageData.assignedAt && (
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium">
                      {t("livreur.assignedAt")}:
                    </span>{" "}
                    {new Date(packageData.assignedAt).toLocaleString()}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Assignment Actions */}
          {!packageData.livreur ? (
            <div className="space-y-3">
              <SelectUser
                role="livreur"
                // query={{ status: "active" }}
                user={selectedLivreur}
                onSelectUser={handleLivreurSelect}
                placeholder={t("livreur.selectALivreur")}
                searchPlaceholder={t("livreur.searchLivreurs")}
                className="w-full"
                isLoading={isLoadingLivreurs}
              />

              {selectedLivreur && (
                <div className="p-2 bg-blue-50 dark:bg-blue-950/20 rounded-md border border-blue-200 dark:border-blue-800">
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    <span className="font-medium">
                      {t("livreur.defaultDeliveryPrice")}:
                    </span>{" "}
                    {getDefaultPrice()} DH
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label
                  htmlFor="deliveryPrice"
                  className="flex items-center gap-2"
                >
                  <DollarSign className="h-4 w-4" />
                  {t("livreur.deliveryPrice")} (DH)
                </Label>
                <Input
                  id="deliveryPrice"
                  type="number"
                  min="0"
                  step="0.01"
                  value={deliveryPrice}
                  onChange={(e) => setDeliveryPrice(e.target.value)}
                  placeholder={`${t("pickup.default")}: ${
                    selectedLivreur ? getDefaultPrice() : 10
                  } DH`}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  {t(
                    "livreur.thisIsTheAmountTheLivreurWillEarnForDeliveringThisPackage"
                  )}
                  {!deliveryPrice && selectedLivreur && (
                    <span className="block mt-1 text-blue-600 dark:text-blue-400">
                      {t("livreur.leaveEmptyToUseDefault")} ({getDefaultPrice()}{" "}
                      DH)
                    </span>
                  )}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                {t(
                  "livreur.toAssignADifferentLivreurFirstUnassignTheCurrentOne"
                )}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t("common.cancel")}
          </Button>

          {packageData.livreur ? (
            <Button
              variant="destructive"
              onClick={handleUnassign}
              disabled={unassignLivreurMutation.isPending}
            >
              {unassignLivreurMutation.isPending
                ? t("pickup.unassigning")
                : t("livreur.unassignLivreur")}
            </Button>
          ) : (
            <Button
              onClick={handleAssign}
              disabled={assignLivreurMutation.isPending || !selectedLivreur}
            >
              {assignLivreurMutation.isPending
                ? t("pickup.assigning")
                : t("livreur.assignLivreur")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default LivreurAssignmentDialog;
